/**
 * Part 23a, tables RP, RI and RN3–RN6: what a paragraph of an export is (by its style's names, so a save that renames
 * style IDs changes nothing), and its runs as re-import reads them: a later reference to a note by the bookmark around
 * it, a picture the file doesn't hold by its "Image Text" run, a note's own mark and a chained note's number taken off
 * its opening. Pure.
 */
import type { FlagKind } from '../../flags.ts';
import type { DraftLeaf, Run } from '../../write.ts';
import { PLAIN_MARKS, runsTitle } from '../../write.ts';
import type { Styles } from '../styles.ts';
import { chapterStart } from './pieces.ts';
import type { Piece } from './pieces.ts';

/** The export's style names (part 22's table ST), lower case as `Styles` gives them. */
export const NAMES = {
  normal: 'normal',
  first: 'first paragraph',
  centered: 'centered',
  sceneBreak: 'scene break',
  picture: 'picture',
  listParagraph: 'list paragraph',
  listContinue: 'list continue',
  listMarker: 'list marker',
  footnoteText: 'footnote text',
  footnoteReference: 'footnote reference',
  /** RN3b, RN5b: LibreOffice's style for a note's own mark, which its save writes as text (and gives a chained opening). */
  footnoteCharacters: 'footnote characters',
  endnoteCharacters: 'endnote characters',
  noteCitation: 'note citation',
  imageText: 'image text',
} as const;

/** RP1: the styles of what the export generates (part 22's principle 2); no export name (null) is none of them. */
const GENERATED: ReadonlySet<string | null> = new Set([
  'chapter number',
  'chapter subtitle',
  'chapter image',
  'title',
  'subtitle',
  'matter author',
  'matter publisher',
  'matter copyright',
  'matter heading',
  'matter text',
  'matter first',
  'matter centered',
  'matter source',
  'matter item',
  'manuscript contact',
  'manuscript byline',
  'header',
]);

const KNOWN: ReadonlySet<string | null> = new Set([...Object.values(NAMES), ...GENERATED]);

/** RP8: the export's quote styles, `Quote` and `Quote n` (n its depth). */
const QUOTE = /^quote(?: (\d+))?$/u;

/** The export's name a style chain (nearest first) is, by the nearest name that is one; null for none. */
export function exportName(names: readonly string[]): string | null {
  return names.find((name) => KNOWN.has(name) || QUOTE.test(name)) ?? null;
}

/** N″2's range: `_ctn<number>_<ID>`. */
const CITATION = /^_ctn(\d{1,9})_\d+$/u;

/** RI7 (deep-fix r1): an "Image Text" run's range, `_ctp<ID>`. */
const PICTURE = /^_ctp\d+$/u;

/** A run of digits, as a note's number is written. */
const DIGITS = /^\d{1,9}$/u;

/** The run when it is text in one of `styles` (re-import gives every text run its source). */
function styled(run: Run | undefined, ...styles: readonly string[]): Extract<Run, { kind: 'text' }> | undefined {
  if (run?.kind !== 'text') return undefined;
  return styles.some((style) => style === run.source?.style) ? run : undefined;
}

/** RN5, RN5b: the styles a chained note's opening number stands in: the export's, or LibreOffice's after a save. */
const OPENINGS = [NAMES.footnoteReference, NAMES.footnoteCharacters, NAMES.endnoteCharacters] as const;

/** A run and its index among the paragraph's runs as read, where the bookmarks sit. */
interface Placed {
  readonly run: Run;
  readonly at: number;
}

/** One no-break space taken off the run at `index` of `runs`, when it is text opening with one (RN3, RN5). */
function dropSpace(runs: Placed[], index: number): void {
  const item = runs[index];
  if (item?.run.kind !== 'text' || !item.run.text.startsWith('\u00a0')) return;
  const text = item.run.text.slice(1);
  if (text === '') runs.splice(index, 1);
  else runs[index] = { run: { ...item.run, text }, at: item.at };
}

/** RN5, RN5b: the number a paragraph's first run opens a chained note with (digits in an opening style, no citation), or null. */
function openingOf(head: Placed | undefined, rangeAt: (at: number) => number): number | null {
  if (head === undefined) return null;
  const run = styled(head.run, ...OPENINGS);
  return run !== undefined && DIGITS.test(run.text) && rangeAt(head.at) < 0 ? Number(run.text) : null;
}

/** What a paragraph's runs are once read: the runs, and the number a chained note's opening gave (RN5), or null. */
export interface ReadRuns {
  readonly runs: readonly Run[];
  readonly opening: number | null;
  /** RN4: the paragraph held only its note's mark or opening, so it shows nothing of the note. */
  readonly markOnly: boolean;
}

/** A range of runs a piece's bookmarks make: from its start's run to its end's, and the number its name carries (0: none). */
interface RunRange {
  readonly from: number;
  readonly to: number;
  readonly number: number;
}

/**
 * RN6, RI7: the ranges of a piece whose bookmarks `shape` names (its first group, when it has one, their number). An
 * empty or inverted one (its end at or before its start) holds no run, so {@link rangeCursor} never gives it.
 */
function runRanges(piece: Piece, shape: RegExp): RunRange[] {
  const starts = new Map<string, { readonly at: number; readonly number: number }>();
  const ranges: RunRange[] = [];
  for (const bookmark of piece.bookmarks) {
    if (bookmark.kind === 'start') {
      const match = shape.exec(bookmark.name);
      if (match !== null && !starts.has(bookmark.id)) starts.set(bookmark.id, { at: bookmark.at, number: Number(match[1] ?? 0) });
      continue;
    }
    const start = starts.get(bookmark.id);
    if (start !== undefined) ranges.push({ from: start.at, to: bookmark.at, number: start.number });
  }
  return ranges;
}

/**
 * CO (deep-fix r7: a scan of every range per run was runs × ranges): a lookup of the range holding a run, asked in run
 * order: the index in `ranges` of the latest-starting range holding the run at `at`, or −1. One sweep over the ranges
 * for all the asks.
 */
function rangeCursor(ranges: readonly RunRange[]): (at: number) => number {
  const order = ranges.map((range, index) => ({ range, index })).sort((a, b) => a.range.from - b.range.from);
  const open: { readonly range: RunRange; readonly index: number }[] = [];
  let next = 0;
  return (at) => {
    for (let item = order[next]; item !== undefined && item.range.from <= at; item = order[++next]) open.push(item);
    while ((open.at(-1)?.range.to ?? Number.POSITIVE_INFINITY) <= at) open.pop();
    return open.at(-1)?.index ?? -1;
  };
}

/**
 * RI7, RN3, RN5, RN6: a piece's runs as re-import reads them. In a note (`note`), its own mark's no-break space and a
 * chained note's opening number are taken off, and in a note's first paragraph (`opens`) the mark LibreOffice's save
 * writes as text (RN3b); anywhere, a later reference becomes a reference `n:<number>` (resolved in its chapter) and the
 * "Image Text" runs of one `_ctp` range (or, with none, standing side by side) a picture the file doesn't hold
 * (destination empty): a save that splits a run (WE21) splits no picture (deep-fix r1).
 */
export function readRuns(piece: Piece, note: boolean, opens = false): ReadRuns {
  const content = piece.paragraph.content;
  const runs: Placed[] = content.runs.map((run, at) => ({ run, at }));
  const ranges = runRanges(piece, CITATION);
  const rangeAt = rangeCursor(ranges);
  const pictureAt = rangeCursor(runRanges(piece, PICTURE));
  let opened = false;
  // RN3: the note's own mark and the no-break space after it.
  const markAt = content.markAt;
  if (note && markAt !== null) {
    opened = true;
    dropSpace(runs, runs.findIndex((item) => item.at >= markAt));
  } else if (opens && styled(runs[0]?.run, NAMES.footnoteCharacters, NAMES.endnoteCharacters) !== undefined) {
    // RN3b: LibreOffice's save writes the note's own mark as its text, in a style of its own.
    opened = true;
    runs.splice(0, 1);
    dropSpace(runs, 0);
  }
  // RN5: a chained note's opening, its number in "footnote reference" alone at the paragraph's start.
  const opening = note ? openingOf(runs[0], rangeAt) : null;
  if (opening !== null) {
    opened = true;
    runs.splice(0, 1);
    dropSpace(runs, 0);
  }
  const out: Run[] = [];
  /** The pictures "Image Text" runs make: where each stands in `out`, its text so far and its link. */
  const pictures: { readonly index: number; alt: string; readonly href: string | null }[] = [];
  /** The range or `w:r` the last run read came from, so one reference or one picture isn't read twice. */
  let last: string | null = null;
  for (const { run, at } of runs) {
    // Only text is a reference or a picture's text, by its character style.
    if (run.kind === 'text') {
      const style = run.source?.style;
      const range = rangeAt(at);
      const citation = style === NAMES.noteCitation || (style === NAMES.footnoteReference && DIGITS.test(run.text));
      if (citation && (range >= 0 || DIGITS.test(run.text))) {
        const key = range >= 0 ? `range:${String(range)}` : null;
        if (key === null || last !== key) out.push({ kind: 'note', key: `n:${String(ranges[range]?.number ?? Number(run.text))}` });
        last = key;
        continue;
      }
      if (style === NAMES.imageText) {
        // Runs side by side with no range (−1) are one picture too.
        const key = `picture:${String(pictureAt(at))}`;
        const previous = last === key ? pictures.at(-1) : undefined;
        if (previous === undefined) {
          pictures.push({ index: out.length, alt: run.text, href: run.href });
          out.push(run);
        } else previous.alt += run.text;
        last = key;
        continue;
      }
    }
    last = null;
    out.push(run);
  }
  // RI7: each picture in its place; a lone no-break space is the export's text for a picture with none.
  for (const { index, alt, href } of pictures) out[index] = { kind: 'image', alt: /^\u00a0*$/u.test(alt) ? '' : alt, destination: '', href };
  return { runs: out, opening, markOnly: opened && isBlank(out) };
}

/** B10: whether runs show nothing: no text but white space, no image, no note. */
export function isBlank(runs: readonly Run[]): boolean {
  return runs.every((run) => run.kind === 'break' || (run.kind === 'text' && run.text.trim() === ''));
}

/** B6, decision 64: runs that are only `#`, `***` or `* * *`. */
function isBreakText(runs: readonly Run[]): boolean {
  if (runs.some((run) => run.kind === 'image' || run.kind === 'note')) return false;
  return ['#', '***', '* * *'].includes(runsTitle(runs));
}

/** A paragraph's list as part 18 reads it (L1–L3). */
export interface ListOf {
  readonly numId: string;
  readonly level: number;
  readonly ordered: boolean;
  readonly start: number;
}

/** What one paragraph is for the chapter (table RP). */
export type Placement =
  /** RP1, RP12, an empty centred block: nothing. */
  | { readonly kind: 'skip' }
  /** RP2: a chapter's heading, with its title's runs. */
  | { readonly kind: 'chapter'; readonly runs: readonly Run[] }
  /** T″: the writer's own subtitle line, offered in the review as a title is. */
  | { readonly kind: 'subtitle'; readonly runs: readonly Run[] }
  | {
      readonly kind: 'block';
      /** The block, or null for an item's marker showing nothing (RP9, F9). */
      readonly block: DraftLeaf | { readonly kind: 'scene-break' } | null;
      /**
       * RC5: a marker that is an empty paragraph when its item holds more after it (`- &nbsp;` then a heading: the export
       * writes the empty paragraph with the marker), and nothing when it doesn't (an empty item, LO8); a "List Marker"
       * (C″9b) is never one.
       */
      readonly empty: boolean;
      /** RP8, RC9: the quotes its style puts it in (0: none). */
      readonly quote: number;
      /** RC4, WE2: its numbering whatever it is (a heading or a quote's paragraph opening an item has one too). */
      readonly numbered: ListOf | null;
      /** L9: a heading's numbering label, which it shows unless the numbering is an item's marker (C″9). */
      readonly label: string | null;
      /** RP11: a centred block if it stands at the top level, a paragraph elsewhere. */
      readonly centerable: boolean;
      /** RN5: a chained note's number, opening it. */
      readonly opening: number | null;
      /** Table SF: what its own style says of its containers. */
      readonly voice: Voice;
    };

/**
 * Table SF: what a paragraph's own style says of its containers: none (`plain`, SF1), a quote's depth (`quote`, SF2), an
 * item of a list (`list`, SF3), a list's later block (`continue`, SF4), or nothing either way (`silent`, SF5).
 */
export type Voice = 'plain' | 'quote' | 'list' | 'continue' | 'silent';

/** What {@link placement} needs beside the piece: the styles, whether it is in a note (and opens it), and the flags. */
export interface PlaceContext {
  readonly styles: Styles;
  readonly note: boolean;
  /** RN3b: the piece is its note's first paragraph. */
  readonly opens?: boolean;
  /** RP1: the piece is in the chapters (in a chapter or a note), where Title and Subtitle are an editor's. */
  readonly chapters?: boolean;
  readonly flag: (kind: FlagKind) => void;
}

/** RP8: the quotes a quote style puts its paragraph in: `Quote n` n, any other one (null, no export name, too). */
function quoteDepth(name: string | null): number {
  return Number(QUOTE.exec(String(name))?.[1] ?? 1);
}

/** RP3: a heading of Word level `level` (2–9) as the source's: one less, at most 6. */
function headingLevel(level: number): number {
  return Math.min(level - 1, 6);
}

/**
 * RP1, CH10 (deep-fix r10): a generated paragraph shows nothing of the book, unless it carries a chapter's `_ctc`: then it
 * absorbed that chapter's heading (Backspace at the heading's start, or Delete at the end of the "Chapter Number" line,
 * joins the two, the number's style kept), so it is the heading, its title the text from where the bookmark starts.
 */
function absorbed(piece: Piece, context: PlaceContext): Placement {
  const start = chapterStart(piece);
  if (start === undefined || context.note) return { kind: 'skip' };
  const content = piece.paragraph.content;
  const tail: Piece = {
    ...piece,
    paragraph: { ...piece.paragraph, content: { ...content, runs: content.runs.slice(start.at), markAt: null } },
    bookmarks: piece.bookmarks.map((bookmark) => ({ ...bookmark, at: Math.max(bookmark.at - start.at, 0) })),
  };
  context.flag('structure');
  return { kind: 'chapter', runs: readRuns(tail, false).runs };
}

/** RI12: the longest line a subtitle holds, as the app stores one; longer is a paragraph joined on. */
const MAX_SUBTITLE = 300;

/** Table RI: a heading line's paragraph as a plain one of the chapter. */
export function unstyled(piece: Piece): Piece {
  return { ...piece, paragraph: { ...piece.paragraph, styleId: undefined } };
}

/** RI9: a "Chapter Image" paragraph without its pictures, plain, when anything else is on it (words, a note); else null. */
function withoutPictures(piece: Piece): Piece | null {
  const { content } = piece.paragraph;
  const runs = content.runs.filter((run) => run.kind !== 'image');
  if (!runs.some((run) => run.kind !== 'text' || run.text.trim() !== '')) return null;
  return unstyled({ ...piece, paragraph: { ...piece.paragraph, content: { ...content, runs } } });
}

/** Table RP: a piece's placement. */
export function placement(piece: Piece, context: PlaceContext): Placement {
  const { paragraph } = piece;
  const name = exportName(context.styles.paragraphNames(paragraph.styleId));
  // RP1, WE7 (deep-fix r9): the export writes Title and Subtitle on the title page alone, before the chapters; in them
  // they are an editor's, paragraphs.
  const editors = context.chapters === true && (name === 'title' || name === 'subtitle');
  // Table RI: what an editor put on a heading's own line is theirs. One plain subtitle comes back as one (read.ts says
  // whether this is its place); anything else is a paragraph of the chapter, flagged, so no word can go unseen.
  if (name === 'chapter subtitle' && chapterStart(piece) === undefined) {
    const { runs } = readRuns(piece, context.note);
    // RI7: nothing in a note is a heading's line; an empty one is nothing at all.
    if (context.note && isBlank(runs)) return { kind: 'skip' };
    const text = runs.reduce((length, run) => length + (run.kind === 'text' ? run.text.length : 0), 0);
    if (!context.note && text <= MAX_SUBTITLE && runs.every((run) => run.kind === 'text' && run.href === null)) return { kind: 'subtitle', runs };
    context.flag('structure');
    return placement(unstyled(piece), context);
  }
  const typed = name === 'chapter image' && chapterStart(piece) === undefined ? withoutPictures(piece) : null;
  if (typed !== null) {
    context.flag('structure');
    return placement(typed, context);
  }
  if (GENERATED.has(name) && !editors) return absorbed(piece, context);
  const read = readRuns(piece, context.note, context.opens === true);
  // RN4: a paragraph of a note's mark or a chained note's number alone shows nothing of the note.
  const numbered = paragraph.list;
  if (read.markOnly) return { kind: 'block', block: null, empty: false, quote: 0, numbered, label: null, centerable: false, opening: read.opening, voice: 'silent' };
  const level = piece.side ? null : paragraph.level;
  const runs = read.runs;
  const blank = isBlank(runs);
  const base = { empty: false, quote: 0, numbered, label: null, centerable: false, opening: read.opening, voice: 'silent' } as const;
  if (level !== null && !context.note) {
    // L9 (part 18, deep-fix r1): a numbered heading's label is its text's start (buildBlocks puts it there).
    const label = paragraph.label;
    // RP2: a chapter heading's label is its title's start.
    if (level === 1) return { kind: 'chapter', runs: label === null ? runs : [{ kind: 'text', text: label, marks: PLAIN_MARKS, href: null }, ...runs] };
    // RP12: the export writes no empty heading; an editor emptied it.
    if (blank) return { kind: 'skip' };
    // RP3: Word's heading 8 and 9 are deeper than Markdown's 6.
    if (level > 7) context.flag('deep-heading');
    return { kind: 'block', block: { kind: 'heading', level: headingLevel(level), runs }, ...base, label };
  }
  if (piece.side) return blank ? { kind: 'skip' } : { kind: 'block', block: { kind: 'paragraph', runs }, ...base, voice: 'plain' };
  const role = paragraph.style.role.kind;
  const quote = role === 'quote' ? quoteDepth(name) : 0;
  const list = role === 'body' ? paragraph.list : null;
  // Read only with no list (`voice` asks for the list first).
  const continues = name === NAMES.listContinue || name === NAMES.listParagraph || name === NAMES.listMarker;
  // A centred block or a break says nothing of its containers, numbered or not (deep-fix r6): its numbering still opens
  // its item (SF3), the ranges give the rest (SF5).
  const block = name === NAMES.sceneBreak || name === NAMES.centered || paragraph.centered;
  // Past the generated, heading, quote, list and block styles decided above, what is left is plain (SF1): Normal, First
  // Paragraph, footnote text, Picture, and a style of no export name.
  const voice: Voice = role === 'quote' ? 'quote' : block ? 'silent' : list !== null ? 'list' : continues ? 'continue' : 'plain';
  const placed = { ...base, quote, voice };
  // RP6: a scene break by its style holding nothing or a break's text, or by that text alone; "Scene Break" holding more
  // (typed into, or a paragraph joined to it: deep-fix r10) is centred text, as the style shows it, none of it lost.
  // SB18 (fix-next 77): an editor's empty paragraph with a rule under it is a break as Import… reads it (the export
  // writes no borders, so none of its own paragraphs is one).
  if (isBreakText(runs) || (blank && (name === NAMES.sceneBreak || paragraph.rule))) return { kind: 'block', block: { kind: 'scene-break' }, ...placed };
  if (name === NAMES.sceneBreak) return { kind: 'block', block: { kind: 'centered', runs }, ...placed };
  if (blank) {
    // RP9, F9: an item's marker alone; RP5: an emptied centred block; else an empty paragraph (C″3).
    if (list !== null || name === NAMES.listMarker) return { kind: 'block', block: null, ...placed, empty: name !== NAMES.listMarker };
    if (name === NAMES.centered) return { kind: 'skip' };
    return { kind: 'block', block: { kind: 'paragraph', runs: [] }, ...placed };
  }
  // RP5.
  if (name === NAMES.centered) return { kind: 'block', block: { kind: 'centered', runs }, ...placed };
  // RP11: a body paragraph centred directly (or by a style of the editor's) is a centred block at the top level only.
  const plain = name === null || name === NAMES.normal || name === NAMES.first;
  return { kind: 'block', block: { kind: 'paragraph', runs }, ...placed, centerable: plain && !context.note && quote === 0 && list === null && paragraph.centered };
}
